import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plane, Calendar, Clock, Trash2, Plus } from 'lucide-react';
import type { FlightSegment } from './useBookingExtraction';
import type { IncidentType } from './types';
import { cn } from '@/lib/utils';

// Extended segment with tracking metadata
export interface EditableSegment extends FlightSegment {
  _id: string;
  _original?: FlightSegment;
  _isNew?: boolean;
  _isDeleted?: boolean;
}

interface AnimatedFlightCardProps {
  segment: EditableSegment;
  index: number;
  mode: 'full' | 'compact';
  isAffected?: boolean;
  incidentType?: IncidentType | null;
  onUpdate?: (id: string, field: keyof FlightSegment, value: string) => void;
  onDelete?: (id: string) => void;
  onClick?: (id: string) => void;
  showTimelineNode?: boolean;
}

// Airline brand colors
const airlineColors: Record<string, string> = {
  LH: '#05164d',
  LX: '#c8102e',
  OS: '#e20613',
  BA: '#075aaa',
  AF: '#002157',
  KL: '#00a1e4',
  UA: '#002244',
  AA: '#0078d2',
  DL: '#c8102e',
  EK: '#d71921',
  QR: '#5c0632',
  SQ: '#f9a825',
  TK: '#c8102e',
  EY: '#9e7e38',
  FR: '#073590',
  U2: '#ff6600',
  EW: '#a6093d',
};

function getAirlineColor(carrier: string): string {
  return airlineColors[carrier?.toUpperCase()] || '#3b82f6';
}

// Check if a specific field was modified
function isFieldModified(segment: EditableSegment, field: keyof FlightSegment): boolean {
  if (!segment._original) return false;
  return segment._original[field] !== segment[field];
}

// Check if any field was modified
function hasAnyModification(segment: EditableSegment): boolean {
  if (segment._isNew) return true;
  if (!segment._original) return false;
  const fields: (keyof FlightSegment)[] = [
    'carrier',
    'flight_no',
    'dep_iata',
    'arr_iata',
    'dep_date',
    'dep_time',
    'arr_time',
    'booking_class_category',
  ];
  return fields.some((field) => isFieldModified(segment, field));
}

// Inline editable field component
interface EditableFieldProps {
  value: string;
  onChange: (value: string) => void;
  isModified: boolean;
  className?: string;
  inputClassName?: string;
  maxLength?: number;
  uppercase?: boolean;
  disabled?: boolean;
}

function EditableField({
  value,
  onChange,
  isModified,
  className = '',
  inputClassName = '',
  maxLength,
  uppercase = false,
  disabled = false,
}: EditableFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = () => {
    if (disabled) return;
    setEditValue(value);
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
    const finalValue = uppercase ? editValue.toUpperCase() : editValue;
    if (finalValue !== value) {
      onChange(finalValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  return (
    <div className={`relative inline-flex items-center ${className}`}>
      {isEditing && !disabled ? (
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          maxLength={maxLength}
          className={`bg-transparent border-b-2 border-blue-500 outline-none ${inputClassName}`}
          style={{ width: `${Math.max(editValue.length, 2)}ch` }}
        />
      ) : (
        <span
          onClick={handleClick}
          className={cn(
            inputClassName,
            !disabled && 'cursor-pointer hover:bg-slate-100 rounded px-0.5 -mx-0.5 transition-colors'
          )}
        >
          {value}
        </span>
      )}
      {/* Amber modification indicator - subtle dot */}
      <AnimatePresence>
        {isModified && !isEditing && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="absolute -top-0.5 -right-1.5 w-1.5 h-1.5 rounded-full bg-amber-400"
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Cabin class options
const CABIN_OPTIONS = ['Economy', 'Premium Economy', 'Business', 'First', 'Suites'];

interface EditableCabinProps {
  value: string;
  onChange: (value: string) => void;
  isModified: boolean;
  disabled?: boolean;
}

function EditableCabin({ value, onChange, isModified, disabled = false }: EditableCabinProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} className="relative">
      <span
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={cn(
          'relative text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 transition-colors',
          !disabled && 'cursor-pointer hover:bg-slate-200'
        )}
      >
        {value}
        <AnimatePresence>
          {isModified && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-amber-400"
            />
          )}
        </AnimatePresence>
      </span>

      <AnimatePresence>
        {isOpen && !disabled && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="absolute top-full right-0 mt-1 z-20 bg-white rounded-lg shadow-lg border border-slate-200 py-1 min-w-[140px]"
          >
            {CABIN_OPTIONS.map((option) => (
              <button
                key={option}
                onClick={() => {
                  onChange(option);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 ${
                  option === value ? 'text-blue-600 font-medium' : 'text-slate-700'
                }`}
              >
                {option}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function AnimatedFlightCard({
  segment,
  index,
  mode,
  isAffected = false,
  incidentType,
  onUpdate,
  onDelete,
  onClick,
  showTimelineNode = false,
}: AnimatedFlightCardProps) {
  const airlineColor = getAirlineColor(segment.carrier);
  const isModified = hasAnyModification(segment);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const isCompact = mode === 'compact';

  // Track first render to only animate on mount, not on mode changes
  const isFirstRender = useRef(true);
  useEffect(() => {
    // Set to false after first render
    isFirstRender.current = false;
  }, []);

  const handleFieldUpdate = (field: keyof FlightSegment) => (value: string) => {
    onUpdate?.(segment._id, field, value);
  };

  const handleClick = () => {
    if (isCompact && onClick) {
      onClick(segment._id);
    }
  };

  // Wrapper to handle timeline node positioning
  const cardContent = (
    <motion.div
      layout
      layoutId={`flight-card-${segment._id}`}
      onClick={handleClick}
      className={cn(
        'relative bg-white rounded-xl overflow-hidden transition-all',
        isCompact ? 'cursor-pointer group' : '',
        isAffected
          ? 'border-amber-400 shadow-lg shadow-amber-400/10 border-2'
          : isModified && !isCompact
          ? 'border-amber-200 shadow-amber-100/50 shadow-sm border'
          : 'border-slate-100 hover:shadow-md shadow-sm border',
        // Add left padding when timeline node is shown
        showTimelineNode && 'ml-6'
      )}
      // Only apply initial animation on first render
      initial={isFirstRender.current ? { opacity: 0, y: 20, scale: 0.98 } : false}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.98, transition: { duration: 0.2 } }}
      transition={{
        layout: { type: 'spring', stiffness: 300, damping: 30 },
        opacity: { duration: 0.2 },
        y: { type: 'spring', stiffness: 400, damping: 30 },
        scale: { type: 'spring', stiffness: 400, damping: 30 },
        delay: isFirstRender.current ? index * 0.05 : 0,
      }}
      whileHover={isCompact ? { x: 4 } : { y: -1 }}
    >
      {/* Left accent border */}
      <motion.div
        layout
        className={cn(
          'absolute left-0 top-0 bottom-0',
          isCompact ? 'w-[3px]' : 'w-1'
        )}
        style={{ backgroundColor: segment._isNew ? '#f59e0b' : airlineColor }}
      />

      <motion.div layout className={cn('pl-4', isCompact ? 'p-3 py-2' : 'p-3')}>
        {/* Main content row */}
        <motion.div layout className="flex items-center justify-between">
          <motion.div layout className="flex items-center gap-3">
            {/* Airline logo - only in full mode */}
            {!isCompact && (
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="w-7 h-7 rounded-md flex items-center justify-center text-white text-xs font-bold relative flex-shrink-0"
                style={{ backgroundColor: airlineColor }}
              >
                <EditableField
                  value={segment.carrier}
                  onChange={handleFieldUpdate('carrier')}
                  isModified={isFieldModified(segment, 'carrier')}
                  inputClassName="text-white text-xs font-bold text-center"
                  maxLength={2}
                  uppercase
                  disabled={isCompact}
                />
              </motion.div>
            )}

            {/* Flight number - show carrier code + flight number */}
            <motion.span
              layout
              className={cn(
                'font-medium text-slate-600 flex items-center gap-0.5',
                isCompact ? 'text-sm font-semibold text-slate-900' : 'text-sm'
              )}
            >
              {isCompact ? (
                <>
                  {segment.carrier}{segment.flight_no}
                </>
              ) : (
                <EditableField
                  value={segment.flight_no}
                  onChange={handleFieldUpdate('flight_no')}
                  isModified={isFieldModified(segment, 'flight_no')}
                  inputClassName="text-sm font-medium text-slate-600"
                  maxLength={5}
                  disabled={isCompact}
                />
              )}
            </motion.span>

            {/* Route: DEP → ARR */}
            <motion.div layout className="flex items-center gap-1.5">
              {isCompact ? (
                <>
                  <span className="font-medium text-sm text-slate-600">{segment.dep_iata}</span>
                  <span className="text-slate-400">→</span>
                  <span className="font-medium text-sm text-slate-600">{segment.arr_iata}</span>
                </>
              ) : (
                <>
                  <EditableField
                    value={segment.dep_iata}
                    onChange={handleFieldUpdate('dep_iata')}
                    isModified={isFieldModified(segment, 'dep_iata')}
                    inputClassName="text-base font-semibold text-slate-900"
                    maxLength={3}
                    uppercase
                    disabled={isCompact}
                  />
                  <Plane className="h-3.5 w-3.5 text-slate-400" />
                  <EditableField
                    value={segment.arr_iata}
                    onChange={handleFieldUpdate('arr_iata')}
                    isModified={isFieldModified(segment, 'arr_iata')}
                    inputClassName="text-base font-semibold text-slate-900"
                    maxLength={3}
                    uppercase
                    disabled={isCompact}
                  />
                </>
              )}
            </motion.div>
          </motion.div>

          {/* Right side */}
          <motion.div layout className="flex items-center gap-2">
            {isCompact ? (
              // Compact mode: just date and time
              <div className="text-xs text-slate-500 text-right">
                <div>{segment.dep_date}</div>
                <div className="text-slate-400">
                  {segment.dep_time} - {segment.arr_time}
                </div>
              </div>
            ) : (
              // Full mode: badges, cabin class, delete button
              <>
                {/* Modified badge */}
                <AnimatePresence>
                  {isModified && (
                    <motion.span
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"
                    >
                      {segment._isNew ? 'New' : 'Edited'}
                    </motion.span>
                  )}
                </AnimatePresence>

                {/* Cabin class */}
                <EditableCabin
                  value={segment.booking_class_category}
                  onChange={handleFieldUpdate('booking_class_category')}
                  isModified={isFieldModified(segment, 'booking_class_category')}
                  disabled={isCompact}
                />

                {/* Delete button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteConfirm(true);
                  }}
                  className="p-1 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </motion.div>
        </motion.div>

        {/* Secondary row: Times and Date - only in full mode */}
        <AnimatePresence>
          {!isCompact && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-50">
                {/* Departure time */}
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3 text-slate-400" />
                  <EditableField
                    value={segment.dep_time}
                    onChange={handleFieldUpdate('dep_time')}
                    isModified={isFieldModified(segment, 'dep_time')}
                    inputClassName="text-xs text-slate-500"
                    maxLength={5}
                    disabled={isCompact}
                  />
                  <span className="text-xs text-slate-300 mx-1">→</span>
                  <EditableField
                    value={segment.arr_time}
                    onChange={handleFieldUpdate('arr_time')}
                    isModified={isFieldModified(segment, 'arr_time')}
                    inputClassName="text-xs text-slate-500"
                    maxLength={5}
                    disabled={isCompact}
                  />
                </div>

                {/* Date */}
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3 text-slate-400" />
                  <EditableField
                    value={segment.dep_date}
                    onChange={handleFieldUpdate('dep_date')}
                    isModified={isFieldModified(segment, 'dep_date')}
                    inputClassName="text-xs text-slate-400"
                    maxLength={10}
                    disabled={isCompact}
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Incident badge - only in compact mode when affected */}
        <AnimatePresence>
          {isCompact && isAffected && incidentType && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className={cn(
                'mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium',
                incidentType === 'delay' && 'bg-amber-100 text-amber-700',
                incidentType === 'cancel' && 'bg-red-100 text-red-700',
                incidentType === 'denied_boarding' && 'bg-orange-100 text-orange-700',
                incidentType === 'other' && 'bg-slate-100 text-slate-700'
              )}
            >
              {incidentType === 'delay' && 'Delayed'}
              {incidentType === 'cancel' && 'Cancelled'}
              {incidentType === 'denied_boarding' && 'Denied Boarding'}
              {incidentType === 'other' && 'Disrupted'}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Delete confirmation overlay - only in full mode */}
      <AnimatePresence>
        {showDeleteConfirm && !isCompact && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-white/95 backdrop-blur-sm flex items-center justify-center rounded-xl"
          >
            <div className="text-center p-4">
              <p className="text-sm text-slate-600 mb-3">Diesen Flug entfernen?</p>
              <div className="flex gap-2 justify-center">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteConfirm(false);
                  }}
                  className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete?.(segment._id);
                  }}
                  className="px-3 py-1.5 text-sm text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                >
                  Entfernen
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );

  // If showing timeline node, wrap in a relative container
  if (showTimelineNode) {
    return (
      <div className="relative">
        {/* Timeline node - positioned to align with the timeline line */}
        <motion.div
          layout
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10"
          initial={isFirstRender.current ? { scale: 0, opacity: 0 } : false}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: isFirstRender.current ? index * 0.05 + 0.1 : 0 }}
        >
          <div
            className={cn(
              'w-8 h-8 rounded-full flex items-center justify-center border-2 bg-white',
              isAffected ? 'border-amber-400' : 'border-slate-200'
            )}
          >
            <Plane
              className={cn(
                'h-4 w-4',
                isAffected ? 'text-amber-500' : 'text-slate-400'
              )}
            />
          </div>
        </motion.div>
        {cardContent}
      </div>
    );
  }

  return cardContent;
}

// Add Flight Button component
export function AddFlightButton({ onClick }: { onClick: () => void }) {
  return (
    <motion.button
      onClick={onClick}
      className="w-full mt-3 py-2 rounded-lg border-2 border-dashed border-slate-200 text-slate-400 hover:border-blue-300 hover:text-blue-500 hover:bg-blue-50/50 transition-colors flex items-center justify-center gap-1.5"
      whileHover={{ scale: 1.005 }}
      whileTap={{ scale: 0.995 }}
    >
      <Plus className="h-3.5 w-3.5" />
      <span className="text-xs font-medium">Flug hinzufügen</span>
    </motion.button>
  );
}
